/* tslint:disable:forbid-import */
import { autorun, computed } from 'mobx';
/* tslint:enable:forbid-import */
import { ObservableMap } from 'mobx';
import { getAttachmentUrlForClassic } from 'owa-attachment-url';
import { ClientAttachmentId } from 'owa-client-ids';
import { IInlineImagePlugin } from 'owa-editor-plugins-list';
import AttachmentUrlType from 'owa-files-url/lib/schema/AttachmentUrlType';
import { INLINEIMAGE_ATTRIBUTE_DATA_CUSTOM } from 'owa-inline-image-consts';
import { getPalette } from 'owa-theme';
import { trace } from 'owa-trace';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import { Editor } from 'roosterjs-editor-core';
import { select } from 'satcheljs/lib/legacy';
import {
    AttachmentModel,
    attachmentStore,
    SmimeAttachmentType,
    isSmimeAttachment,
} from 'owa-attachment-model-store';
import {
    InlineAttachmentState,
    InlineAttachmentStatus,
    lazyGetImageDimensionsFromFile,
    lazySetInlineAttachmentStatus,
} from 'owa-attachment-well-data';
import {
    lazyCacheInlineImage,
    lazyClearInlineImageStore,
    lazyLogImageUpload,
    lazyLogImageLoadError,
    lazyLoadFromAttachment,
    lazyLoadFromAttachmentServiceUrl,
    lazyLoadFromProxy,
} from 'owa-inline-image-loader';
import {
    AttachmentFile,
    AttachmentFileList,
    AttachmentFileType,
    Base64InlineImageFile,
    createLocalComputerFiles,
    LocalComputerFile,
} from 'owa-attachment-file-types';
import {
    cleanupImageCopyHandler,
    handleImageCopy,
} from 'owa-inline-image-loader/lib/utils/ImageCopyHandler';
import {
    BeforePasteEvent,
    ContentChangedEvent,
    InsertOption,
    ContentPosition,
    PasteOption,
    PluginEvent,
    PluginEventType,
    PositionType,
} from 'roosterjs-editor-types';

const ADD_INLINE_IMAGE_TO_STORE_IE_TIMEOUT_LENGTH = 1000;
const PASTED_IMAGE_NAME_PREFIX = 'pastedImage';
const PASTED_IMAGE_NAME_SUFFIX = '.png';
const PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME = 'data-loadPending';

// Inline image CID fix
const CID_PREFIX = 'cid:';

// The image proxy endpoint attribute
const ATTRIBUTE_IMAGEPROXYENDPOINT = 'data-imageproxyendpoint';

// The content change source for addins
const ADDIN_CONTENT_CHANGE_SOURCE = 'Addins';

// The content change source for SetContent
const SETCONTENT_CONTENT_CHANGE_SOURCE = 'SetContent';

// The content change source for upgrading compose
const UPGRADE_CHANGE_SOURCE = 'UpgradeCompose';

// The context used for inline image upload
const CONTEXT_UPLOAD = 'Upload';

// The context used for S/MIME inline
const SMIME_INLINE = 'SmimeInline';

// The context used for inline image load (load from existing content, draft, forward etc.)
const CONTEXT_COMPOSE = 'Compose';

// The context used for inline image load by moving from attachment well
const CONTEXT_MOVEFROMATTACHMENTWELL = 'MoveFromAttachmentWell';

// File type
const FILETYPE_ATTACHMENT = 'Attachment';

// Default place holder dimension
const DEFAULT_PLACEHOLDER_WIDTH = 320;
const DEFAULT_PLACEHOLDER_HEIGHT = 220;

// Error
const ERROR_UNSUPPORTED_FILETYPE = 'UnsupportedFileType';
const ERROR_NULL_ATTACHMENT_URL = 'NullAttachmentUrl';

// Paste image
const SAFARI_IMAGE_BLOB_PREFIX = 'blob:';
const WEBKIT_FAKE_URL_IMAGE_PREFIX = 'webkit-fake-url:';
const LOCAL_FILE_REFERENCE_REGEX = new RegExp('^file:///[a-zA-Z]:', 'i');
const BASE64_IMAGE_REGEX = new RegExp('^data:(image\\/\\w+);base64,([A-Za-z0-9+/=]+?)$', 'i');

export interface InlineImagePluginState {
    attachmentStore: ObservableMap<string, AttachmentModel>;
}

interface PastedBase64InlineImageFile extends Base64InlineImageFile {
    imageNode: HTMLImageElement;
}

enum ImageStatus {
    Started = 0,
    PlaceholderShown = 1,
}

const INSERT_OPTION: InsertOption = {
    position: ContentPosition.SelectionStart,
    updateCursor: true,
    replaceSelection: true,
    insertOnNewLine: false,
};

export type CreateAttachments = (
    files: AttachmentFileList,
    isInline: boolean,
    shouldShare: boolean,
    isHiddenInline: boolean,
    processInlineImageBeforeUploadFunc: (temporaryId: string, file: AttachmentFile) => void
) => void;

export type DeletePendingInlineAttachment = (placeholderId: string) => void;

/*
 * Notes: all potential calling orders are listed below.
 * These functions in the class are called asyncly:
 * A: insertPlaceholderImage called on get image dimension callback, in the error case it return dimensions as 0 * 0
 * B: processInlineImageAfterUploadSuccess called from createattachment response success
 * B': onImageError called from createattachment response failure (C/C' will not happen in this case)
 * C: onImageLoad called from actual image onload
 * C': onImageError called from actual image onerror
 * C/C' is bound to happen after B, but the order of A and B/C is uncertain
 *
 * So we have potential orders as below (Please refer to InlineImagePluginTest for expected results):
 * 1: A, B, C (placeholder was inserted before createattachment succeeded)
 * 2: A, B' (placeholder was inserted before createattachment failed)
 * 3: A, B, C' (placeholder was inserted before createattachment succeeded, but actual image failed to load)
 *
 * 4. B, C, A (createattachment succeeded before placeholder was inserted)
 * 5. B, C', A (createattachment succeeded before placeholder was inserted, but actual image failed to load)
 * 6. B', A (createattachment failed before placeholder was inserted)
 *
 * 7. B, A, C (createattachment succeeded before placeholder was inserted, but actual image was loaded after)
 * 8. B, A, C' (createattachment succeeded before placeholder was inserted, but actual image was loaded after and failed)
 *
 * Also if A happens before B/C, placeholder can be removed by user, call it -A.
 * Note that -A can not happen after B', C, C'.
 * So additional potential orders are
 * 9. A, -A, B, C/C' (placeholder was inserted then deleted by user before createattachment succeeded, then actual image was loaded/failed to load)
 * 10. A, -A, B' (placeholder was inserted then deleted by user before createattachment failed)
 * 11. A, B, -A, C/C' (placeholder was inserted before createattachment succeeded, then deleted by user before actual image was loaded/failed to load)
 * 12. B, A, -A, C/C' (createattachment succeeded, then placeholder was inserted and deleted by user before actual image was loaded/failed to load)
 */
export default class InlineImagePlugin implements IInlineImagePlugin {
    private editor: Editor;
    private imageStatusMap: {
        [temporaryId: string]: {
            status: ImageStatus;
            placeholder?: HTMLElement;
        };
    };
    private autorunDisposer: any;
    private observer: MutationObserver;
    private onCopyDisposer: () => void;

    public getName() {
        return 'InlineImage';
    }

    public onPluginEvent(event: PluginEvent): void {
        if (event.eventType == PluginEventType.BeforePaste) {
            this.processInlineImage(<BeforePasteEvent>event);
        } else if (event.eventType == PluginEventType.ContentChanged) {
            const eventSource: string = (<ContentChangedEvent>event).source;
            if (eventSource == ADDIN_CONTENT_CHANGE_SOURCE) {
                this.convertAllContentIdToAttachmentUrl();
            } else if (
                eventSource == SETCONTENT_CONTENT_CHANGE_SOURCE ||
                eventSource == UPGRADE_CHANGE_SOURCE
            ) {
                this.loadInlineImages();
                this.loadExternalImages();
            }
        }
    }

    constructor(
        private readonly getInlineAttachmentStates: () => InlineAttachmentState[],
        private readonly createAttachments: CreateAttachments,
        private readonly deleteInlineAttachments: DeletePendingInlineAttachment,
        private readonly convertContentIdToAttachmentUrl: (
            cidOrName: string
        ) => { url: string; contentId: string },
        state?: InlineImagePluginState
    ) {
        // InlineImageStore keys dataURIs by originalsrc. Since originalsrc is only guaranteed to be unique within a message,
        // clear the store to avoid any false positives.
        lazyClearInlineImageStore.importAndExecute();
        this.processInlineImageAfterUploadSuccess = select<InlineImagePluginState>({
            attachmentStore: () => attachmentStore().attachments,
        })(this.processInlineImageAfterUploadSuccess);
    }

    public initialize(editor: Editor): void {
        this.editor = editor;
        this.imageStatusMap = {};
        const inlineAttachments = this.getInlineAttachmentStates();

        const inlineAttachmentStatusComputed = computed(() => {
            return inlineAttachments.map(inlineAttachment => inlineAttachment.status);
        });

        this.onCopyDisposer = this.editor.addDomEventHandler('copy', this.onCopy);

        this.observer = new MutationObserver(this.processNodeDeleted);
        this.autorunDisposer = autorun(() => {
            const computed = inlineAttachmentStatusComputed.get();
            computed.forEach((status, index) => {
                const attachment = inlineAttachments[index];
                if (attachment.isHiddenInline) {
                    return;
                }
                if (
                    status == InlineAttachmentStatus.CreateAttachmentSucceeded ||
                    status == InlineAttachmentStatus.CreateAttachmentFailed ||
                    status == InlineAttachmentStatus.ConvertAttachmentInlineSucceeded
                ) {
                    if (
                        status == InlineAttachmentStatus.CreateAttachmentSucceeded ||
                        status == InlineAttachmentStatus.ConvertAttachmentInlineSucceeded
                    ) {
                        this.processInlineImageAfterUploadSuccess(
                            attachment.attachmentId,
                            attachment.placeholderId,
                            attachment.skipProcessInlineImage
                        );
                    } else {
                        this.onImageError(
                            null /* imageUrl */,
                            attachment.placeholderId,
                            null /* TargetElement */
                        );
                    }

                    // Complete the inline attachment.
                    lazySetInlineAttachmentStatus.importAndExecute(
                        attachment,
                        InlineAttachmentStatus.Completed
                    );
                }
            });
        });
    }

    public dispose(): void {
        this.editor = null;
        this.observer.disconnect();
        this.imageStatusMap = null;
        this.autorunDisposer();
        cleanupImageCopyHandler();
        if (this.onCopyDisposer) {
            this.onCopyDisposer();
            this.onCopyDisposer = null;
        }
    }

    public async processInlineImageBeforeUpload(temporaryId: string, file: AttachmentFile) {
        // The editor or inline image plugin could already have been disposed by the time this callback is executed
        if (!this.editor || !this.imageStatusMap) {
            return;
        }

        lazyLogImageUpload
            .import()
            .then(imageUploadLogger => imageUploadLogger(file.fileType.toString()));
        if (file.fileType == AttachmentFileType.Base64InlineImage) {
            this.imageStatusMap[temporaryId] = {
                status: ImageStatus.PlaceholderShown,
                placeholder: (file as PastedBase64InlineImageFile).imageNode,
            };
        } else if (file.fileType == AttachmentFileType.Local) {
            const fileObject = file.fileObject;
            this.imageStatusMap[temporaryId] = { status: ImageStatus.Started };
            (await lazyGetImageDimensionsFromFile.import())(fileObject, (height, width) =>
                this.insertPlaceholderImage(temporaryId, file.name, height, width)
            );
        } else {
            lazyLogImageLoadError
                .import()
                .then(imageLoadErrorLogger =>
                    imageLoadErrorLogger(
                        file.fileType.toString() /* type */,
                        '' /* howLoad */,
                        ERROR_UNSUPPORTED_FILETYPE /* error */,
                        '' /* statusCode */,
                        CONTEXT_UPLOAD /* context */
                    )
                );
        }
    }

    public processInlineImageAfterUploadSuccess = (
        attachmentId: ClientAttachmentId,
        temporaryId: string,
        skipProcessInlineImage: boolean,
        state?: InlineImagePluginState
    ): void => {
        if (skipProcessInlineImage || !this.editor) {
            // This function is called asyncly. This instance may have been disposed.
            return;
        }
        const attachment = state.attachmentStore.get(attachmentId.Id).model;
        const isSmime = isSmimeAttachment(attachmentId.Id);
        const downloadUrl = isSmime
            ? this.getAttachmentUrlForSmime(attachment)
            : getAttachmentUrlForClassic(attachmentId, AttachmentUrlType.Preview);
        if (downloadUrl) {
            this.replacePlaceholderImage(
                temporaryId,
                downloadUrl,
                attachment.ContentId,
                attachment.Size,
                attachment.ContentType,
                isSmime
            );
        } else {
            lazyLogImageLoadError
                .import()
                .then(imageLoadErrorLogger =>
                    imageLoadErrorLogger(
                        FILETYPE_ATTACHMENT /* type */,
                        '' /* howLoad */,
                        ERROR_NULL_ATTACHMENT_URL /* error */,
                        '' /* statusCode */,
                        isSmime ? SMIME_INLINE : CONTEXT_UPLOAD /* context */
                    )
                );
        }
    };

    public insertPlaceholderImage(
        temporaryId: string,
        fileName: string,
        placeholderImageWidth: number,
        placeholderImageHeight: number
    ): void {
        if (!this.editor) {
            // This function is called asyncly. This instance may have been disposed.
            return;
        }

        const entry = this.imageStatusMap[temporaryId];
        if (entry && entry.status == ImageStatus.Started) {
            this.editor.addUndoSnapshot();
            const placeholderNode = this.generatePlaceholderNode(
                temporaryId,
                fileName,
                placeholderImageWidth,
                placeholderImageHeight
            );
            this.editor.insertNode(placeholderNode, INSERT_OPTION);
            const actualWidth = placeholderNode.offsetWidth;
            if (actualWidth < placeholderImageWidth) {
                // Resize placeholder node proportionally.
                placeholderNode.style.height = Math.ceil(
                    (actualWidth / placeholderImageWidth) * placeholderImageHeight
                ).toString();
            }
            entry.placeholder = placeholderNode;
            entry.status = ImageStatus.PlaceholderShown;

            // Start observing on the parent for the placeholder node. This is so that we will be notified when the node is removed
            // so we can cleanup the pending upload. Note: according to the documentaion for MutationObserver calling .observe multiple
            // times on with same element has no effect.
            const contentEditableParent = this.getContentEditableParent(placeholderNode);
            if (contentEditableParent) {
                trace.info('InlineImagePlugin: Start observing on content editable div');
                this.observer.observe(contentEditableParent, { subtree: true, childList: true });
            }
        }
    }

    public onSmimeUploadSuccess(
        oldContentId: string,
        attachmentId: ClientAttachmentId,
        newContentId: string
    ): boolean {
        const elements = this.getImageElementsByContentId(oldContentId);
        // If element is found, we set the originalSrc. The src attribute is anyways stripped off
        // when data is sent to server, so we need not set that using lazyLoadFromAttachmentServiceUrl
        if (elements.length > 0) {
            const targetElement = elements[0];
            targetElement.setAttribute('originalSrc', CID_PREFIX + newContentId);
            return true;
        } else {
            trace.warn(
                'S/MIME onSmimeUploadSuccess : Inline image target element not found for content id : ' +
                    oldContentId
            );
            return false;
        }
    }

    public onImageLoad(temporaryId: string, targetElement: HTMLImageElement): void {
        if (!this.editor) {
            // This function is called asyncly. This instance may have been disposed.
            return;
        }

        // Disable native image selection on the image element
        targetElement.setAttribute('unselectable', 'on');
        // IE11 still lets you tab to an image that is unselectable. Set tabindex so that the focus moves from the
        // inline image directly to the ribbon.
        targetElement.setAttribute('tabindex', '-1');
        targetElement.style.msUserSelect = 'none';
        targetElement.style.webkitUserSelect = 'none';

        const entry = this.imageStatusMap[temporaryId];
        if (entry && entry.status == ImageStatus.PlaceholderShown) {
            // If placeholder has been inserted
            const sourceElement = entry.placeholder;
            if (this.editor.contains(sourceElement)) {
                // If placeholder is found, replace it.
                sourceElement.removeAttribute(PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME);

                this.editor.replaceNode(sourceElement, targetElement);

                // After replacing the node to insert the image, move the insertion point so its immediately after the
                // newly inserted image
                this.editor.select(targetElement, PositionType.After);
            } else {
                // If placeholder is not found, i.e. user has deleted it
                // Directly insert it at cursor.
                this.editor.insertNode(targetElement, INSERT_OPTION);
            }
        } else {
            // If placeholder has not been inserted, directly insert at cursor.
            this.editor.insertNode(targetElement, INSERT_OPTION);
        }

        // Generate and store a dataURI for the copy handler and the reading pane's local lie scenario
        if (isBrowserIE()) {
            // IE triggers onload for images before the image is actually in the DOM
            // so we need to call it from a timer to let the image finish loading.
            setTimeout(function() {
                lazyCacheInlineImage.import().then(cacher => cacher(targetElement));
            }, ADD_INLINE_IMAGE_TO_STORE_IE_TIMEOUT_LENGTH);
        } else {
            lazyCacheInlineImage.import().then(cacher => cacher(targetElement));
        }

        delete this.imageStatusMap[temporaryId];
        this.addEditorUndoSnapshotIfNecessary();
        this.disconnectMutationObserverIfNecessary();
        targetElement.onload = null;
    }

    public onImageError(
        imageUrl: string,
        temporaryId: string,
        targetElement: HTMLImageElement
    ): void {
        if (!this.editor) {
            // This function is called asyncly. This instance may have been disposed.
            return;
        }

        const entry = this.imageStatusMap[temporaryId];
        if (entry && entry.status == ImageStatus.PlaceholderShown) {
            // If placeholder has been inserted
            const sourceElement = entry.placeholder;
            if (sourceElement) {
                // If placeholder is found. remove it on error.
                this.editor.deleteNode(sourceElement);
            }
        }

        delete this.imageStatusMap[temporaryId];
        this.addEditorUndoSnapshotIfNecessary();
    }

    private onCopy = (event: UIEvent) => {
        handleImageCopy(event.currentTarget as HTMLDivElement);
    };

    private processInlineImage(event: BeforePasteEvent) {
        if (event.pasteOption == PasteOption.PasteHtml) {
            const images = event.fragment.querySelectorAll('img');
            const imageCount = images.length;
            const base64InlineImageFiles: AttachmentFile[] = [];
            let base64FileIndex = 0;
            for (let i = 0; i < imageCount; i++) {
                const image = images[i];
                const imageSrc = image.getAttribute('src');
                // Check for images with no src or images pasted from Safari which don't have usable sources
                if (
                    !imageSrc ||
                    imageSrc.indexOf(SAFARI_IMAGE_BLOB_PREFIX) === 0 ||
                    imageSrc.indexOf(WEBKIT_FAKE_URL_IMAGE_PREFIX) === 0 ||
                    imageSrc.match(LOCAL_FILE_REFERENCE_REGEX)
                ) {
                    // Remove all images with no src or local file src
                    image.parentNode.removeChild(image);
                    continue;
                }

                // Check for images with src as base64 data uri
                const base64Match = imageSrc.match(BASE64_IMAGE_REGEX);
                if (base64Match && base64Match.length === 3) {
                    const base64InlineImageFile: PastedBase64InlineImageFile = {
                        name:
                            PASTED_IMAGE_NAME_PREFIX +
                            'base64' +
                            base64FileIndex++ +
                            PASTED_IMAGE_NAME_SUFFIX,
                        size: 0, // default to 0 since we cannot get size of image with src as data uri.
                        type: base64Match[1],
                        dataUri: base64Match[2],
                        imageNode: image,
                        fileType: AttachmentFileType.Base64InlineImage,
                    };
                    base64InlineImageFiles.push(base64InlineImageFile);
                }
            }

            if (base64InlineImageFiles.length > 0) {
                this.createAttachments(
                    base64InlineImageFiles,
                    true /* isInline */,
                    false /* shouldShare */,
                    false /* isHiddenInline */,
                    this.processInlineImageBeforeUpload.bind(this)
                );
            }
        } else if (event.pasteOption == PasteOption.PasteImage) {
            while (event.fragment.firstChild) {
                event.fragment.removeChild(event.fragment.firstChild);
            }
            event.pasteOption = PasteOption.PasteHtml;
            this.createAttachmentsForLocalFiles(event.clipboardData.image);
        }
    }

    private getImageElementsByContentId(contentId: string): HTMLElement[] {
        return this.editor.queryElements(`img[originalsrc^="${CID_PREFIX + contentId}"]`);
    }

    private replacePlaceholderImage(
        temporaryId: string,
        imageUrl: string,
        contentId: string,
        size: number,
        contentType: string,
        isSmime: boolean = false
    ): void {
        let targetElement,
            isImageElementAlreadyPresent = false;
        if (isSmime) {
            const elements = this.getImageElementsByContentId(contentId);
            if (elements.length > 0) {
                targetElement = elements[0];
                isImageElementAlreadyPresent = true;
            }
        }
        if (!targetElement) {
            targetElement = this.editor.getDocument().createElement('img');
            targetElement.setAttribute('originalSrc', 'cid:' + contentId);
            targetElement.setAttribute('size', size.toString());
            targetElement.setAttribute('contentType', contentType);
            targetElement.style.maxWidth = '100%';
        }

        lazyLoadFromAttachmentServiceUrl.import().then(logger =>
            logger(
                targetElement,
                imageUrl,
                isSmime ? SMIME_INLINE : CONTEXT_UPLOAD,
                isImageElementAlreadyPresent
                    ? null
                    : (imageElement: HTMLImageElement) => {
                          this.onImageLoad(temporaryId, imageElement);
                      },
                isImageElementAlreadyPresent
                    ? null
                    : (imageElement: HTMLImageElement) => {
                          this.onImageError(imageUrl, temporaryId, imageElement);
                      }
            )
        );
    }

    private generatePlaceholderNode(
        temporaryId: string,
        fileName: string,
        placeholderWidth: number,
        placeholderHeight: number
    ): HTMLDivElement {
        const placeholderDiv = this.editor.getDocument().createElement('div');
        const style = placeholderDiv.style;
        if (placeholderWidth == 0 || placeholderHeight == 0) {
            placeholderWidth = DEFAULT_PLACEHOLDER_WIDTH;
            placeholderHeight = DEFAULT_PLACEHOLDER_HEIGHT;
        }
        style.display = 'inline-block';
        style.maxWidth = '100%';
        style.borderWidth = '1px';
        style.borderStyle = 'dashed';
        style.borderColor = getPalette().themePrimary;
        style.boxSizing = 'border-box';
        style.width = placeholderWidth.toString() + 'px';
        style.height = placeholderHeight.toString() + 'px';
        placeholderDiv.title = fileName;
        placeholderDiv.contentEditable = 'false';
        placeholderDiv.setAttribute('tempid', temporaryId);
        placeholderDiv.setAttribute(PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME, 'true');
        return placeholderDiv;
    }

    private createAttachmentsForLocalFiles(file: File) {
        const localComputerFile: LocalComputerFile = createLocalComputerFiles([file])[0];
        if (!localComputerFile.name) {
            localComputerFile.name = PASTED_IMAGE_NAME_PREFIX + 'file' + PASTED_IMAGE_NAME_SUFFIX;
        }
        this.createAttachments(
            [localComputerFile],
            true /* isInline */,
            false /* shouldShare */,
            false /* isHiddenInline */,
            this.processInlineImageBeforeUpload.bind(this)
        );
    }

    private addEditorUndoSnapshotIfNecessary() {
        if (Object.keys(this.imageStatusMap).length == 0) {
            // if there're pending inline attachments (either started or placeholder shown),
            // do not add editor undo snapshot yet,
            // add it when the last pending inline attachment is done
            // broadcast contentChangedEvent to ensure the snapshot actually gets added
            this.editor.triggerContentChangedEvent('InlineImage');
            this.editor.addUndoSnapshot();
        }
    }

    private disconnectMutationObserverIfNecessary() {
        // If there aren't any pending inline attachments then we don't need the mutation observer to
        // listen for anything. It will start observing again if a new placeholder is added
        if (Object.keys(this.imageStatusMap).length === 0) {
            trace.info('InlineImagePlugin: Disconnect mutation observer');
            this.observer.disconnect();
        }
    }

    // Gets the nearest parent element for the given element with the contenteditable attribute set to true
    private getContentEditableParent(element: HTMLElement): HTMLElement {
        while (element) {
            if (element.contentEditable == 'true') {
                return element;
            }

            element = element.parentElement;
        }

        return null;
    }

    // Process the mutation record looking for placeholder images that have been deleted
    private processNodeDeleted = (nodes: MutationRecord[]) => {
        for (let i = 0; i < nodes.length; i++) {
            const removedNodes = nodes[i].removedNodes;
            if (removedNodes) {
                for (let j = 0; j < removedNodes.length; j++) {
                    const removedNode = removedNodes.item(j);

                    // Check the removed node to see if it is a placeholder that is still pending load
                    const temporaryIdForRemovedNode = this.getPendingPlaceholderForNode(
                        removedNode
                    );
                    if (temporaryIdForRemovedNode) {
                        trace.info(
                            'InlineImagePlugin: Pending image placeholder removed from DOM. Deleting the attachment.'
                        );
                        this.deleteInlineAttachments(temporaryIdForRemovedNode);
                        if (this.imageStatusMap[temporaryIdForRemovedNode]) {
                            delete this.imageStatusMap[temporaryIdForRemovedNode];
                        }
                    }
                }
            }
        }

        this.disconnectMutationObserverIfNecessary();
    };

    // Checks to see if the given node is a pending placeholder, and it if is return its tempid.
    // Otherwise recursively check all of its children to find a pending placholder.
    private getPendingPlaceholderForNode(node: Node): string {
        if (
            node.nodeName.toLowerCase() == 'div' &&
            (<HTMLElement>node).hasAttribute('tempid') &&
            (<HTMLElement>node).hasAttribute(PLACEHOLDER_LOAD_PENDING_ATTRIBUTE_NAME)
        ) {
            return (<HTMLElement>node).getAttribute('tempid');
        } else if (node.hasChildNodes()) {
            for (let i = 0; i < node.childNodes.length; i++) {
                const tempid = this.getPendingPlaceholderForNode(node.childNodes[i]);
                if (tempid) {
                    return tempid;
                }
            }
        }

        return null;
    }

    private convertAllContentIdToAttachmentUrl() {
        this.editor.queryElements(`img[src^="${CID_PREFIX}"]`, (image: HTMLImageElement) => {
            const cidOrName = image.src.substr(CID_PREFIX.length);
            const imageAttributes = this.convertContentIdToAttachmentUrl(cidOrName);
            if (imageAttributes) {
                image.setAttribute('originalsrc', CID_PREFIX + imageAttributes.contentId);
                lazyLoadFromAttachmentServiceUrl
                    .import()
                    .then(logger =>
                        logger(image, imageAttributes.url, CONTEXT_MOVEFROMATTACHMENTWELL)
                    );
            }
        });
    }

    private loadInlineImages() {
        this.editor.queryElements(
            `img[${INLINEIMAGE_ATTRIBUTE_DATA_CUSTOM}]`,
            (image: HTMLImageElement) => {
                lazyLoadFromAttachment
                    .import()
                    .then(loader => loader(image, CONTEXT_COMPOSE, false /* usePlaceHolder */));
            }
        );
    }

    private loadExternalImages() {
        this.editor.queryElements(
            `img[${ATTRIBUTE_IMAGEPROXYENDPOINT}]`,
            (image: HTMLImageElement) => {
                lazyLoadFromProxy.import().then(loader => loader(image, CONTEXT_COMPOSE));
            }
        );
    }

    private getAttachmentUrlForSmime(attachment: SmimeAttachmentType): string {
        const { Content, ContentType } = attachment;
        return 'data:' + ContentType + ';base64,' + Content;
    }
}
